import { ChangeEvent, useCallback, MouseEventHandler } from 'react';
import styled from 'styled-components';
import FormattedNumber from 'react-number-format';
import { useQuoteBuilderView } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/view';
import {
  useQuoteBuilderModalMethods,
  useQuoteBuilderModalState,
} from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/modal-context';
import {
  nepal,
  paradigmLightBlue,
  ripeLemon,
} from '@paradigm/design-system/src/colors';
import { useQuoteBuilderActions } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/form-context';
import {
  LegsTableContainer as BaseContainer,
  Header,
  Row,
  TableHeader,
} from '#/components/rfq-common/LegsTable';
import { ClickableSpan } from '#/components/UnifiedRfqs/components//ClickableSpan';
import { LegPriceView } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/views/legsPrices';
import { track } from '@paradigm/analytics/src/analytics';
import { formatPrecision, isValidDecimal } from '@paradigm/utils/formatDecimal';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';
import { useSubmitted } from '@paradigm/features/src/forms/submitted-context';
import { getPriceSide } from '@paradigm/features/src/unified-rfqs/ui/rfqs/quotes-functions';
import { Side } from '@paradigm/features/src/unified-rfqs/entities/shared';
import { useTrackAction } from '@paradigm/features/src/analytics';

function InputLegsPricesTable() {
  return (
    <>
      <MarkPriceLegs />
      <InputLegsPrice />
    </>
  );
}

export default InputLegsPricesTable;

function MarkPriceLegs() {
  const { legsPrices } = useQuoteBuilderView();

  return (
    <Container css="padding-top: 42px; width: 90px;">
      <TableHeader>
        <MarkPriceRow>
          <span>Mark</span>
        </MarkPriceRow>
      </TableHeader>
      {legsPrices.legs.map((leg, index) => (
        <MarkPriceRowValue index={index} key={leg.key} legPriceView={leg} />
      ))}
    </Container>
  );
}

interface MarkPriceRowValueProps {
  readonly index: number;
  readonly legPriceView: LegPriceView;
}

function MarkPriceRowValue({ index, legPriceView }: MarkPriceRowValueProps) {
  const { markPrice, side: legSide, precision, markPriceIv } = legPriceView;
  const isMarkIvEmpty = markPriceIv === '';
  const shouldRenderMarkIv = !isMarkIvEmpty;
  const isMarkEmpty = markPrice === '';
  const changeLegPrice = useChangeLegPriceAction();
  const { side: modalSide } = useQuoteBuilderModalState();

  const side = getPriceSide(modalSide, legSide);
  const trackAction = useTrackAction();
  const selectMarkPrice = useCallback(() => {
    trackAction('select mark price');
    changeLegPrice({ index, side, value: markPrice });
  }, [trackAction, changeLegPrice, index, side, markPrice]);

  return !shouldRenderMarkIv ? (
    <MarkPriceRow>
      <ClickableSpan
        onClick={selectMarkPrice}
        data-testid={`leg-${index + 1}-mark-price`}
      >
        {isMarkEmpty ? NEGATIVE_SIGN : formatPrecision(markPrice, precision)}
      </ClickableSpan>
    </MarkPriceRow>
  ) : (
    <MarkPriceRowIv>
      <ClickableSpan
        onClick={selectMarkPrice}
        data-testid={`leg-${index + 1}-mark-price`}
      >
        {isMarkEmpty ? NEGATIVE_SIGN : formatPrecision(markPrice, precision)}
      </ClickableSpan>
      <MarkPriceIv data-testid={`leg-${index + 1}-mark-price-iv`}>
        {markPriceIv}%
      </MarkPriceIv>
    </MarkPriceRowIv>
  );
}

function InputLegsPrice() {
  const { legPricingEnabled, isMarkPricePrefill } = useQuoteBuilderModalState();
  const { setSelectedSide } = useQuoteBuilderModalMethods();
  const { legsPrices, price } = useQuoteBuilderView();
  const showWarning = legPricingEnabled;

  if (!legPricingEnabled) return null;

  return (
    <Container
      showWarning={showWarning}
      css={`
        width: 130px;
      `}
      aria-label="strategy leg prices"
    >
      <Header css="font-size: 0.8rem;">Leg Prices</Header>
      <TableHeader>
        <InputPriceRow>
          <span>Bid</span>
          <span>Ask</span>
        </InputPriceRow>
      </TableHeader>
      {legsPrices.legs.map((leg, index) => (
        <InputPriceRow key={leg.key}>
          <LegPriceInput
            side="BUY"
            isInvalid={false}
            index={index}
            totalPrice={price.value[leg.side === 'BUY' ? 0 : 1]}
            leg={leg}
            onClick={() => setSelectedSide('BUY')}
          />
          <LegPriceInput
            side="SELL"
            index={index}
            isInvalid={false}
            totalPrice={price.value[leg.side === 'BUY' ? 1 : 0]}
            leg={leg}
            onClick={() => setSelectedSide('SELL')}
          />
        </InputPriceRow>
      ))}
      {isMarkPricePrefill && <UsingMarkPriceWarning />}
    </Container>
  );
}

interface PriceInputProps {
  readonly index: number;
  readonly leg: LegPriceView;
  readonly isInvalid?: boolean;
  readonly side: 'BUY' | 'SELL';
  readonly totalPrice: string | undefined;
  readonly onClick: MouseEventHandler<HTMLInputElement>;
}

function LegPriceInput({
  index,
  leg,
  side,
  totalPrice,
  onClick,
  isInvalid = false,
}: PriceInputProps) {
  const { legPricingEnabled } = useQuoteBuilderModalState();
  const { changeLegPrice } = useQuoteBuilderActions();
  const { didSubmit } = useSubmitted();

  const priceType = side === 'BUY' ? 'bid' : 'ask';
  const trackAction = useTrackAction();
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      trackAction('change leg price', { index, side });
      changeLegPrice({ index, side, value: event.target.value });
    },
    [changeLegPrice, index, side, trackAction],
  );

  const legPriceValue = leg[priceType];

  const isInvalidSubmit =
    didSubmit && !isPriceEmpty(totalPrice) && isPriceEmpty(legPriceValue);

  return (
    <FormatterInput
      showWarning={isInvalidSubmit || isInvalid}
      data-testid={`leg-${index + 1}-${priceType}-price`}
      aria-label={`leg ${index + 1} ${priceType} price`}
      disabled={!legPricingEnabled}
      thousandSeparator
      decimalScale={leg.precision}
      value={legPriceValue}
      allowNegative={false}
      placeholder={leg.placeholder}
      onChange={handleChange}
      onClick={onClick}
    />
  );
}

export function useChangeLegPriceAction() {
  const { changeLegPrice } = useQuoteBuilderActions();
  return useCallback(
    (args: { index: number; side: Side; value: string }) => {
      track(`drfqs__markPrice__clicked`, { markPrice: args.value });
      changeLegPrice(args);
    },
    [changeLegPrice],
  );
}

const isPriceEmpty = (value: string | null | undefined) => {
  return value == null || !isValidDecimal(value);
};

const Container = styled(BaseContainer)<{ showWarning?: boolean }>`
  position: relative;
  margin-left: 5px;

  ${(props) => props.showWarning === true && `border: solid 1px ${ripeLemon};`}
`;

const UsingMarkPriceWarning = styled.div.attrs({
  children: '*Using mark prices as baseline',
})`
  position: absolute;
  bottom: -19px;
  right: 0px;
  white-space: nowrap;
  font-size: 11px;
  opacity: 0.7;
  color: ${ripeLemon};
`;

const MarkPriceRow = styled(Row)`
  grid-template-columns: auto;
`;

const InputPriceRow = styled(Row)`
  grid-template-columns: auto auto;
`;

const FormatterInput = styled(FormattedNumber).withConfig<{
  showWarning: boolean;
}>({
  shouldForwardProp: (prop) => prop !== 'showWarning',
})`
  color: white;
  border: none;
  margin: 0.5rem 0;
  border-bottom: solid 2px ${paradigmLightBlue}88;
  width: 100%;
  background-color: unset;

  &:focus {
    outline: none;
    border-bottom: solid 2px ${paradigmLightBlue};
  }

  &:disabled {
    border-bottom: none;
  }

  ${(props) => props.showWarning && `border-bottom: solid 1px ${ripeLemon};`}
`;

export const MarkPriceIv = styled.span`
  height: 12px;
  width: 34px;
  opacity: 0.3;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 11px;
  color: #ffffff;
`;

export const MarkPriceRowIv = styled.div`
  line-height: 14px;
  font-size: 13px;
  color: ${nepal};
  font-family: 'IBM Plex Sans';
  display: grid;
  padding: 0px 12px;
  padding-top: 10px;
  align-items: center;
`;
